import React, { Suspense, useRef } from 'react';
import { Row, Col, Table } from 'reactstrap';

import LoadingScreen from 'src/componentes/LoadingScreen';
import guid from 'src/utils/guid';
import DropDown from 'src/paginas/item/dropdown';
import PolaridadeGrafico from './polaridade-grafico';

const Grafico = React.lazy(() => import('src/componentes/chart'));

const TAMANHO_MINIMO_CLIQUE = 20;
const INTERVALO_CLIQUE_DUPLICADO = 500;
const ATRIBUTO_AREA_CLIQUE = 'data-area-clique';
const SVG_NS = 'http://www.w3.org/2000/svg';

function GraficoItem({
  grafico,
  item,
  height,
  filtro,
  dataClick,
  handleCloseModal,
  isConsultaExterna,
  graficoColunasEmpilhadas,
  transparent,
  showTable = false,
  showChart = true,
  graficoPizza = false,
  tituloPersonalizado = null,
}) {
  const ultimoPlotClicado = useRef(null);

  const renderColunas = () => {
    const categories = grafico.tabelaGrafico.colunas;

    return (
      categories &&
      categories.map((category, index) => <th key={index}>{category}</th>)
    );
  };

  const renderLabelItem = (valor) => <span>{valor}</span>;

  const renderDados = () => {
    const itens = grafico.tabelaGrafico.dados;

    return itens.map((linha, indexLinha) => (
      <tr key={indexLinha}>
        {linha.item.map((x, indexColuna) => (
          <td key={indexColuna}>
            {x.item ? (
              <a tabIndex="-1">
                <DropDown
                  showMenu={!isConsultaExterna}
                  handleCloseModal={handleCloseModal}
                  filtro={filtro}
                  item={x.item}
                  renderLabel={() => renderLabelItem(x.valor)}
                />
              </a>
            ) : (
              x.valor
            )}
          </td>
        ))}
      </tr>
    ));
  };

  const dataPlot = (ev, dados) => {
    // Recupera o DataSet do gráfico
    let dataset = null;

    //se não for de colunas empilhadas
    if (!graficoColunasEmpilhadas) {
      dataset =
        grafico && grafico.dataSet && grafico.dataSet.length > 0
          ? grafico.dataSet[0]
          : null;
    }
    //se for golunas empilhadas repuera o dataset clicado
    else {
      dataset =
        grafico &&
        grafico.dataSet &&
        grafico.dataSet.length > 0 &&
        ev &&
        ev.data
          ? grafico.dataSet[ev.data.datasetIndex]
          : null;
    }

    // Verifica se tem dataSet
    if (dataset) {
      // Tenta recuperar o objeto clicado
      const dado = dataset.data[dados.index];
      // Chama o método de data clicado passando o Dado recuperado
      if (dataClick) {
        !graficoColunasEmpilhadas
          ? dataClick(dado, ev)
          : dataClick(dado, ev.data.categoryLabel, ev.data.datasetName);
      }
    }
  };

  const aoClicarNoPlot = (ev, dados) => {
    const chave = `${dados?.datasetIndex ?? 0}-${dados?.index}`;
    const agora = Date.now();
    const ultimo = ultimoPlotClicado.current;

    ultimoPlotClicado.current = { chave, tempo: agora };

    if (
      ultimo &&
      ultimo.chave === chave &&
      agora - ultimo.tempo < INTERVALO_CLIQUE_DUPLICADO
    )
      return;

    dataPlot(ev, dados);
  };

  const criarAreaDeClique = (plot, index, datasetIndex) => {
    const x = parseFloat(plot.getAttribute('x'));
    const y = parseFloat(plot.getAttribute('y'));
    const largura = parseFloat(plot.getAttribute('width'));
    const altura = parseFloat(plot.getAttribute('height'));

    if (!isFinite(x) || !isFinite(y)) return null;

    const larguraArea = Math.max(largura, TAMANHO_MINIMO_CLIQUE);
    const alturaArea = Math.max(altura, TAMANHO_MINIMO_CLIQUE);
    const dados = {
      index,
      datasetIndex,
      categoryLabel: grafico?.categories?.[0]?.category?.[index]?.label,
      datasetName: grafico?.dataSet?.[datasetIndex]?.seriesname,
    };
    const area = document.createElementNS(SVG_NS, 'rect');

    area.setAttribute(ATRIBUTO_AREA_CLIQUE, '1');
    area.setAttribute('x', x + largura / 2 - larguraArea / 2);
    area.setAttribute('width', larguraArea);
    area.setAttribute('y', y + altura / 2 - alturaArea / 2);
    area.setAttribute('height', alturaArea);
    area.setAttribute('fill', 'rgba(0,0,0,0)');
    area.style.cursor = 'pointer';
    area.addEventListener('click', () =>
      aoClicarNoPlot({ data: dados }, dados)
    );

    return area;
  };

  const ampliarAreaDeClique = (chart) => {
    if (!dataClick) return;

    const svg = chart?.options?.containerElement?.querySelector('svg');

    if (!svg) return;

    svg
      .querySelectorAll(`rect[${ATRIBUTO_AREA_CLIQUE}]`)
      .forEach((area) => area.remove());

    const gruposDeColunas = Array.from(
      svg.querySelectorAll('g[class*="-plots"] g[class*="plot-group"]')
    )
      .map((grupo) => ({
        grupo,
        plots: Array.from(grupo.children).filter((no) => no.tagName === 'rect'),
      }))
      .filter(({ plots }) => plots.length > 0);

    const dataSet = grafico?.dataSet || [];
    const datasets = graficoColunasEmpilhadas ? dataSet : dataSet.slice(0, 1);

    if (!datasets.length || gruposDeColunas.length !== datasets.length) return;

    gruposDeColunas.forEach(({ grupo, plots }, datasetIndex) => {
      if ((datasets[datasetIndex].data || []).length !== plots.length) return;

      plots.forEach((plot, index) => {
        const largura = parseFloat(plot.getAttribute('width'));
        const altura = parseFloat(plot.getAttribute('height'));

        if (!isFinite(largura) || !isFinite(altura)) return;

        const area = criarAreaDeClique(plot, index, datasetIndex);

        if (area) grupo.appendChild(area);
      });
    });
  };

  const montarEventos = () => ({
    dataPlotClick: aoClicarNoPlot,
    renderComplete: (ev) => ampliarAreaDeClique(ev?.sender),
  });

  const renderTituloPersonalizado = () => {
    const {
      titulo,
      unidadeMedidaPersonalizada,
      background,
      marginBottom,
      polaridade,
    } = tituloPersonalizado;

    return (
      <div
        style={{
          fontSize: 14,
          overflow: 'hidden',
          width: '100%',
          minHeight: 30,
          backgroundColor: background ? background : 'transparent',
          padding: '5px 10px',
          textAlign: 'left',
          marginBottom: marginBottom != null ? marginBottom : 15,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div
            style={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              maxWidth: '30vw',
            }}
          >
            {titulo}
          </div>

          <PolaridadeGrafico polaridade={polaridade} item={item} />
        </div>
        {tituloPersonalizado.unidadeMedida ? (
          <span style={{ fontSize: 12 }}>
            {item &&
              item.unidadeMedida &&
              item.tipo &&
              item.tipo.id != 4 &&
              item.unidadeMedida.descricao}
          </span>
        ) : (
          unidadeMedidaPersonalizada != null && (
            <span style={{ fontSize: 12 }}>{unidadeMedidaPersonalizada}</span>
          )
        )}
      </div>
    );
  };

  const dadosGrafico =
    grafico && grafico.dataSet && grafico.dataSet.length > 0 ? true : false;
  const tipoDoGrafico =
    grafico && grafico.tipoGrafico != null ? grafico.tipoGrafico : 'MSCombi2D';
  let config;

  if (tipoDoGrafico == 'Bubble' || tipoDoGrafico == 'Scatter') {
    config = {
      width: '100%',
      height: showTable
        ? height
          ? height / 2
          : '400'
        : height
        ? height - 10
        : '400',
      type: tipoDoGrafico,
      containerBackgroundOpacity: grafico && grafico.containerBackgroundOpacity,
      dataFormat: item ? null : 'json',
      dataSource: {
        chart: {
          ...(grafico && grafico.chart),
          caption:
            tituloPersonalizado == null
              ? item
                ? item && item.nome
                : ' '
              : ' ',
        },
        annotations:
          grafico && grafico.annotations ? grafico.annotations : null,
        categories:
          grafico && grafico.usarCategorias
            ? grafico.categories
            : [
                {
                  verticalLineDashed: '1',
                  verticalLineDashLen: '1',
                  verticalLineDashGap: '1',
                  verticalLineThickness: '1',
                  verticalLineColor: '#000000',
                  category: grafico.dataSet && grafico.dataSet[0].data,
                },
              ],
        dataSet: dadosGrafico ? grafico.dataSet : null,
      },
      events: montarEventos(),
    };
  }

  if (
    tipoDoGrafico == 'msbar2d' ||
    tipoDoGrafico == 'Bar2D' ||
    tipoDoGrafico == 'MSCombi2D' ||
    tipoDoGrafico == 'StackedColumn2d' ||
    tipoDoGrafico == 'mscombidy2d' ||
    tipoDoGrafico == 'stackedbar2d' ||
    tipoDoGrafico == 'mscolumn2d' ||
    tipoDoGrafico == 'doughnut2d'
  )
    config = {
      width: '100%',
      height: showTable
        ? height
          ? height / 2
          : '400'
        : height
        ? height - 10
        : '400',
      type: tipoDoGrafico,
      containerBackgroundOpacity: grafico && grafico.containerBackgroundOpacity,
      dataFormat: item ? null : 'json',
      dataSource: {
        chart: {
          ...(grafico && grafico.chart),
          caption:
            tituloPersonalizado == null
              ? item
                ? item && item.nome
                : ' '
              : ' ',
          chartRightMargin: 60,
        },
        annotations:
          grafico && grafico.annotations ? grafico.annotations : null,
        categories: grafico && grafico.categories,
        data: dadosGrafico ? grafico.dataSet && grafico.dataSet[0].data : null,
        dataset: dadosGrafico ? grafico.dataSet : null,
      },
      events: montarEventos(),
    };

  if (graficoPizza || tipoDoGrafico == 'Pie2D')
    config = {
      width: '100%',
      height: height ? height - 50 : '400',
      type:
        grafico && grafico.tipoGrafico != null
          ? grafico.tipoGrafico
          : 'mscombi2d',
      containerBackgroundOpacity: grafico && grafico.containerBackgroundOpacity,
      dataFormat: item ? null : 'json',
      dataSource: {
        chart: {
          ...(grafico && grafico.chart),
          captionPadding: 0,
          chartLeftMargin: 0,
          chartRightMargin: 0,
          chartTopMargin: 0,
          chartBottomMargin: 0,
          legendNumColumns: 2,
          legendItemFontSize: 11,
          caption:
            tituloPersonalizado == null
              ? item
                ? item && item.nome
                : ' '
              : ' ',
          bgColor: transparent ? '#FFFFFF00' : grafico?.chart.bgColor, // Setting the background color to transparent
          bgAlpha: transparent ? '0' : grafico?.chart.bgAlpha,
          //"subcaption": grafico && grafico.tipoTabela === "Desvio" ? grafico && grafico.chart && grafico.chart.subcaption : null,
        },
        annotations:
          grafico && grafico.annotations ? grafico.annotations : null,
        categories: grafico && grafico.categories,
        data: dadosGrafico
          ? grafico.dataSet[0] && grafico.dataSet[0].data
          : null,
      },
    };

  if (tipoDoGrafico == 'Pareto2D')
    config = {
      width: '100%',
      height: showTable
        ? height
          ? height / 2
          : '400'
        : height
        ? height - 10
        : '400',
      type: tipoDoGrafico,
      containerBackgroundOpacity: grafico && grafico.containerBackgroundOpacity,
      dataFormat: item ? null : 'json',
      dataSource: {
        chart: {
          ...(grafico && grafico.chart),
          caption:
            tituloPersonalizado == null
              ? item
                ? item && item.nome
                : ' '
              : ' ',
        },
        annotations:
          grafico && grafico.annotations ? grafico.annotations : null,
        categories: grafico && grafico.categories,
        data: dadosGrafico ? grafico.dataSet && grafico.dataSet[0].data : null,
        dataset: dadosGrafico ? grafico.dataSet : null,
      },
    };

  return (
    <Suspense fallback={<LoadingScreen />}>
      {showChart && (
        <Row id={`relatorioPag${!item ? '' : item && item.id}`}>
          <Col id="graficoValoresItem" style={{ borderSpacing: 0, padding: 2 }}>
            {tituloPersonalizado && renderTituloPersonalizado()}
            <Grafico key={guid()} {...config} />
          </Col>
        </Row>
      )}
      <div>
        <Row>
          <Col md={12}>
            {showTable && grafico && grafico.tabelaGrafico.dados && (
              <Table
                hover
                size="sm"
                style={{
                  background: '#FFF',
                  marginTop: 15,
                  overflow: 'visible',
                }}
              >
                <thead>
                  <tr>{renderColunas()}</tr>
                </thead>
                <tbody>{renderDados()}</tbody>
              </Table>
            )}
          </Col>
        </Row>
      </div>
    </Suspense>
  );
}

export default GraficoItem;
